import type { RiskBoard } from "@station/contracts";
import { Alert, Button, Card, FormField, Input, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import {
  CounterpartyPage,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { queryKeys } from "../../../lib/query.js";

const searchSchema = z.object({
  ...companySearchShape,
  near: z.coerce.number().int().min(1).max(99).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/cari/risk")({
  validateSearch: (search) => searchSchema.parse(search),
  component: RiskBoardPage,
});

function RiskBoardPage() {
  const { t } = useTranslation();
  return (
    <CounterpartyPage
      title={t("menu.counterpartyRisk")}
      description={t("counterparties.riskBoard.description")}
    >
      {({ companyId }) => <Board companyId={companyId} />}
    </CounterpartyPage>
  );
}

type Entry = RiskBoard["overLimit"][number];

function Board({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const nearLimitPercent = Route.useSearch().near ?? 80;
  const [draft, setDraft] = useState(String(nearLimitPercent));
  useEffect(() => setDraft(String(nearLimitPercent)), [nearLimitPercent]);
  const parsed = /^\d{1,2}$/.test(draft) ? Number.parseInt(draft, 10) : Number.NaN;
  const draftValid = parsed >= 1 && parsed <= 99;

  const query = { companyId, nearLimitPercent };
  const board = useQuery({
    queryKey: queryKeys.counterparties(companyId, "risk-board", query),
    queryFn: async () => (await api.GET("/counterparty-reports/risk-board", { params: { query } })).data!,
    placeholderData: keepPreviousData,
  });
  const apply = (): void => {
    if (draftValid)
      void navigate({
        search: (previous) => ({ ...previous, near: parsed === 80 ? undefined : parsed }),
        replace: true,
      });
  };

  return (
    <div className="grid gap-5">
      <form
        className="flex flex-wrap items-end gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <FormField
          label={t("counterparties.riskBoard.nearPercent")}
          description={t("counterparties.riskBoard.nearPercentHint")}
          error={draftValid ? undefined : t("counterparties.riskBoard.nearPercentInvalid")}
          className="w-64"
        >
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              maxLength={2}
              className="tabular"
              value={draft}
              onChange={(event) => setDraft(event.target.value.replace(/\D/g, ""))}
              onBlur={apply}
            />
          )}
        </FormField>
        <Button type="submit" variant="outline" className="mb-5" disabled={!draftValid}>
          {t("counterparties.aging.apply")}
        </Button>
      </form>

      {board.error ? (
        <Alert tone="danger">{errorMessage(board.error)}</Alert>
      ) : !board.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <div className="grid gap-5 xl:grid-cols-3">
          <BoardList
            title={t("counterparties.riskBoard.overLimit")}
            empty={t("counterparties.riskBoard.overLimitEmpty")}
            entries={board.data.overLimit}
            mode="limit"
          />
          <BoardList
            title={t("counterparties.riskBoard.nearLimit", { percent: board.data.nearLimitPercent })}
            empty={t("counterparties.riskBoard.nearLimitEmpty")}
            entries={board.data.nearLimit}
            mode="limit"
          />
          <BoardList
            title={t("counterparties.riskBoard.overdue")}
            empty={t("counterparties.riskBoard.overdueEmpty")}
            entries={board.data.overdue}
            mode="overdue"
          />
        </div>
      )}
    </div>
  );
}

function BoardList({
  title,
  empty,
  entries,
  mode,
}: {
  title: string;
  empty: string;
  entries: readonly Entry[];
  mode: "limit" | "overdue";
}): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <Card className="grid content-start gap-3 p-4" aria-labelledby={headingId}>
      <h2 id={headingId} className="flex items-center justify-between gap-2 text-base font-semibold">
        {title}
        <StatusBadge tone={entries.length === 0 ? "success" : mode === "limit" ? "danger" : "warning"}>
          {t("counterparties.riskBoard.count", { count: entries.length })}
        </StatusBadge>
      </h2>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <SimpleTable
          caption={title}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("counterparties.fields.counterparty")}
              </th>
              {mode === "limit" ? (
                <>
                  <th scope="col" className={thRightClass}>
                    {t("counterparties.riskBoard.riskOverLimit")}
                  </th>
                  <th scope="col" className={thRightClass}>
                    {t("counterparties.riskBoard.usage")}
                  </th>
                </>
              ) : (
                <>
                  <th scope="col" className={thRightClass}>
                    {t("counterparties.fields.overdueReceivable")}
                  </th>
                  <th scope="col" className={thRightClass}>
                    {t("counterparties.riskBoard.oldest")}
                  </th>
                </>
              )}
            </tr>
          }
        >
          {entries.map((entry) => (
            <tr key={entry.counterpartyId} className="border-t align-top">
              <th scope="row" className={`${tdClass} text-left font-normal`}>
                <Link
                  to="/cari/$id"
                  params={{ id: entry.counterpartyId }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  <span className="font-mono text-xs font-semibold">{entry.code}</span> {entry.name}
                </Link>
                {entry.isBlocked ? (
                  <>
                    {" "}
                    <StatusBadge tone="danger">{t("counterparties.badges.blocked")}</StatusBadge>
                  </>
                ) : null}
              </th>
              {mode === "limit" ? (
                <>
                  <td className={tdRightClass}>
                    <MoneyText value={entry.totalRisk} />
                    <br />
                    <span className="text-xs text-muted-foreground">
                      / <MoneyText value={entry.creditLimit} className="text-xs" />
                    </span>
                  </td>
                  <td className={`${tdRightClass} font-semibold`}>
                    {entry.usagePercent === null ? "—" : `%${entry.usagePercent}`}
                  </td>
                </>
              ) : (
                <>
                  <td className={tdRightClass}>
                    <MoneyText value={entry.overdueReceivable} />
                  </td>
                  <td className={tdRightClass}>
                    {t("counterparties.openItems.overdueDays", { days: entry.oldestOverdueDays })}
                  </td>
                </>
              )}
            </tr>
          ))}
        </SimpleTable>
      )}
    </Card>
  );
}
